import { useEffect, useRef, useState } from 'react';
import { useReturnFocus } from './Dialog';
import * as RadixDialog from '@radix-ui/react-dialog';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { MAX_CANVAS_TITLE_LENGTH, type Canvas, type PublicUser } from '@avslack/shared';
import {
  useCanvases,
  useCreateCanvas,
  useDeleteCanvas,
  useReorderCanvases,
  useUpdateCanvas,
} from '../data/canvases';
import { formatDay } from '../lib/format';
import { useUsers } from '../data/users';
import { LazyCanvasEditor } from './LazyCanvasEditor';
import { ListEditor } from './ListEditor';
import { useCreateList, useDeleteList, useLists } from '../data/lists';
import type { List } from '@avslack/shared';
import { Icon } from './Icon';
import { useConfirm } from './Confirm';
import { useIsDesktop } from '../lib/media';
import { useBackToClose } from '../lib/back-to-close';
import { ShareCanvasDialog } from './ShareCanvasDialog';

/**
 * A csatorna kozos lapjai: CANVASOK es LISTAK.
 *
 * Balra a lista, jobbra a megnyitott lap. Szandekosan NEM a kozos `Dialog`
 * keretben: az legfeljebb `max-w-2xl`, egy szerkesztheto dokumentumnak viszont
 * a kepernyo java resze kell, kulonben minden masodik szo tordelodik.
 *
 * ─── Miert egy panelben a ketto? ────────────────────────────────────────────
 *
 * Mert a felhasznalo szamara ugyanaz a kerdes: "mi tartozik meg ehhez a
 * csatornahoz azon kivul, amit beszeltunk". Hogy az valaminek a leirasa
 * (canvas) vagy egy kipipalhato feladatsor (lista), az csak forma. Ket kulon
 * panel ket helyen keresteine ugyanazt.
 */
export function CanvasPanel({
  channelId,
  channelLabel,
  me,
  canEdit,
  personal = false,
  initialCanvasId,
  onClose,
}: {
  channelId: string;
  channelLabel: string;
  me: PublicUser;
  /** Csak a csatorna tagjai szerkeszthetnek - a szerver ugyanezt kenyszeriti ki. */
  canEdit: boolean;
  /** A SAJAT jegyzetek panelje: innen oszthatok meg a canvasok, es ide jon a "Velem megosztva". */
  personal?: boolean;
  initialCanvasId?: string | null;
  onClose: () => void;
}) {
  // Telefonon a vissza-mozdulat ezt a panelt zarja (K2).
  useBackToClose(true, onClose);
  const { data: canvases, isLoading } = useCanvases(channelId);
  const { data: lists, isLoading: listsLoading } = useLists(channelId);

  /**
   * Mi van nyitva.
   *
   * A tipus is kell, nem csak az azonosito: a ket fajta lap kulon halmaz, es
   * egy puszta azonositorol nem lehetne eldonteni, melyik szerkesztot nyissuk.
   */
  const [selected, setSelected] = useState<{ kind: 'canvas' | 'list'; id: string } | null>(
    initialCanvasId ? { kind: 'canvas', id: initialCanvasId } : null,
  );

  /*
    ASZTALON, ha nincs kivalasztva semmi, az elso lap nyilik meg: ott a ket
    oszlop egymas mellett all, es egy ures jobb oldal csak elpazarolt hely.

    MOBILON viszont nem valasztunk magunktol. Ott egyszerre EGY oszlop latszik,
    tehat egy automatikus valasztas egybol egy dokumentumba dobna a
    felhasznalot - a listat, amibol valasztani akart, meg sem latna.
  */
  const isDesktop = useIsDesktop();

  useEffect(() => {
    if (selected || !isDesktop) return;
    if (canvases && canvases.length > 0)
      return setSelected({ kind: 'canvas', id: canvases[0]!.id });
    if (lists && lists.length > 0) setSelected({ kind: 'list', id: lists[0]!.id });
  }, [canvases, lists, selected, isDesktop]);

  const selectedCanvas =
    selected?.kind === 'canvas'
      ? (canvases?.find((item) => item.id === selected.id) ?? null)
      : null;
  const selectedList =
    selected?.kind === 'list' ? (lists?.find((item) => item.id === selected.id) ?? null) : null;

  // Bezaraskor a fokusz vissza a nyito elemre (lasd Dialog.tsx).
  const returnFocus = useReturnFocus(true);
  return (
    <RadixDialog.Root open onOpenChange={(open) => !open && onClose()}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="animate-fade-in fixed inset-0 z-50 bg-black/40 backdrop-blur-[2px]" />
        <RadixDialog.Content
          onCloseAutoFocus={returnFocus}
          /*
            MOBILON teljes kepernyo, asztalon lebego ablak.

            Egy telefonon a kerettel es a lekerekitett sarkokkal elvesztegetett
            hely pont az, ami a tartalomnak kellene.
          */
          className={[
            'animate-fade-rise fixed z-50 flex flex-col overflow-hidden',
            'inset-0 h-full w-full rounded-none border-0',
            // A teljes kepernyos mobil nezetben a notch es a home-csik ala ne loghasson tartalom.
            'pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]',
            'pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)] sm:p-0',
            'sm:inset-auto sm:left-1/2 sm:top-[4vh] sm:h-[92vh] sm:w-[calc(100vw-2rem)]',
            'sm:max-w-6xl sm:-translate-x-1/2 sm:rounded-card sm:border sm:border-border-soft',
            'bg-surface-raised text-ink shadow-(--shadow-modal)',
          ].join(' ')}
        >
          {/*
            A parbeszed CIMSZINTJEI: ez az egyetlen h1, alatta h2 az oldalsav es
            a megnyitott canvas cime. A Radix alapbol h2-t adna, ami az oldalsav
            h2-je utan egy kesobbi h1-gyel felborult volna.
          */}
          <RadixDialog.Title asChild>
            <h1 className="sr-only">{channelLabel} — canvasok és listák</h1>
          </RadixDialog.Title>
          <RadixDialog.Description className="sr-only">
            A csatornához tartozó közös dokumentumok és teendőlisták.
          </RadixDialog.Description>

          <div className="flex min-h-0 min-w-0 flex-1">
            {/*
              MOBILON egyszerre EGY oszlop latszik.

              Amig nincs kivalasztva semmi, a lista tolti ki a kepernyot; egy
              lapra kattintva a lista helyere lep a tartalom, es a fejlec
              vissza gombja hozza vissza. Ugyanaz a minta, mint a fo
              feluleten - egy 390 keppont szeles telefonon ket oszlop egymas
              mellett nem fer el ugy, hogy barmelyik hasznalhato maradjon.
            */}
            <CanvasList
              hidden={!isDesktop && selected !== null}
              onClose={onClose}
              channelId={channelId}
              channelLabel={channelLabel}
              canvases={canvases}
              lists={lists}
              isLoading={isLoading || listsLoading}
              selected={selected}
              canEdit={canEdit}
              personal={personal}
              onSelect={setSelected}
              onDeleted={(kind, id) =>
                setSelected((current) =>
                  current?.kind === kind && current.id === id ? null : current,
                )
              }
            />

            <div
              className={[
                'min-w-0 flex-1 flex-col',
                !isDesktop && selected === null ? 'hidden' : 'flex',
              ].join(' ')}
            >
              <CanvasHeader
                canvas={selectedCanvas}
                me={me}
                canEdit={canEdit}
                canShare={personal && selectedCanvas?.viaShare === false}
                onClose={onClose}
                onBack={!isDesktop && selected !== null ? () => setSelected(null) : undefined}
              />

              {selectedCanvas && (
                /*
                  A `key` a canvas azonositoja: canvas-valtaskor a szerkeszto
                  UJRA epul. Enelkul a TipTap peldany a regi Yjs dokumentumhoz
                  maradna kotve, es a masik canvas tartalmat irnank felul.
                */
                <LazyCanvasEditor
                  key={selectedCanvas.id}
                  canvas={selectedCanvas}
                  me={me}
                  canEdit={canEdit}
                />
              )}

              {selectedList && (
                <ListEditor
                  key={selectedList.id}
                  list={selectedList}
                  channelId={channelId}
                  canEdit={canEdit}
                />
              )}

              {!selectedCanvas && !selectedList && (
                <EmptyState
                  hasAny={(canvases?.length ?? 0) + (lists?.length ?? 0) > 0}
                  isLoading={isLoading || listsLoading}
                />
              )}
            </div>
          </div>
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}

/**
 * A bal oldali lap-lista: canvasok es listak egyutt.
 *
 * Ket kulon szakaszban, mert masra valok - de EGY helyen, mert a felhasznalo
 * ugyanazt keresi bennuk: mi tartozik meg ehhez a csatornahoz.
 */
function CanvasList({
  hidden,
  onClose,
  channelId,
  channelLabel,
  canvases,
  lists,
  isLoading,
  selected,
  canEdit,
  personal,
  onSelect,
  onDeleted,
}: {
  /** Mobilon elrejtve, amikor egy lap van nyitva - lasd a panel megjegyzeset. */
  hidden: boolean;
  onClose: () => void;
  channelId: string;
  channelLabel: string;
  canvases: Canvas[] | undefined;
  lists: List[] | undefined;
  isLoading: boolean;
  selected: { kind: 'canvas' | 'list'; id: string } | null;
  canEdit: boolean;
  personal: boolean;
  onSelect: (selection: { kind: 'canvas' | 'list'; id: string }) => void;
  onDeleted: (kind: 'canvas' | 'list', id: string) => void;
}) {
  const createCanvas = useCreateCanvas(channelId);
  const removeCanvas = useDeleteCanvas(channelId);
  const reorder = useReorderCanvases(channelId);
  const createList = useCreateList(channelId);
  const removeList = useDeleteList(channelId);
  const confirm = useConfirm();
  const { nameOf } = useUsers();

  /** Mit hozunk letre eppen - ez donti el, hova kerul a beirt cim. */
  const [creating, setCreating] = useState<'canvas' | 'list' | null>(null);
  const [title, setTitle] = useState('');

  /**
   * Tipusvalasztas utan a kurzor a cim mezobe kerul.
   *
   * A mezon ott van az `autoFocus`, megsem oda kerult a fokusz - KET dolog
   * huzta el onnan, egymas utan:
   *
   *  1. A Radix menu zaraskor VISSZAADJA a fokuszt a gombnak, ami megnyitotta.
   *  2. Ezt megallitva sem a mezon kotott ki: a panel egy parbeszed, aminek
   *     fokuszcsapdaja van, es az behuzta magahoz.
   *
   * Ezert nem eleg megallitani a visszaadast - kifejezetten oda kell tenni a
   * kurzort, a menu zaro-lepese UTAN (innen a kepkocka-varakozas).
   *
   * A visszaadast csak akkor allitjuk meg, ha MI akarjuk mashova tenni: Escape
   * -re vagy kivul kattintva marad a Radix viselkedese, ott a gombra
   * visszaterni a helyes.
   */
  const focusTitle = useRef(false);
  const titleRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!creating) return;

    /*
      Addig probaljuk, amig tenyleg a mezon nem all a kurzor.

      Ket fokuszkezelo dolgozik itt egyszerre, es mindketto a SAJAT
      utemezeseben: a menu zaraskor visszaadna a fokuszt a gombnak, a
      parbeszed fokuszcsapdaja pedig magahoz huzza, ha a fokusz a semmibe
      kerul. Egyetlen `focus()` hivas barmelyikuk utan erkezhet - ahogy
      merve is ezt lattuk: a mezo ott volt, a kurzor megis a parbeszeden.

      Ezert nem idozitest talalgatunk, hanem ELLENORIZZUK az eredmenyt, es
      par kepkockan at ujraprobaljuk. Amint sikerult, abbahagyjuk.
    */
    let hatra = 5;
    let id = 0;

    const probal = () => {
      const mezo = titleRef.current;
      if (!mezo) return;
      if (document.activeElement === mezo) return;

      mezo.focus();
      hatra -= 1;
      if (hatra > 0) id = requestAnimationFrame(probal);
    };

    id = requestAnimationFrame(probal);
    return () => cancelAnimationFrame(id);
  }, [creating]);

  const submit = () => {
    const trimmed = title.trim();
    if (!trimmed || !creating) return;

    const done = (kind: 'canvas' | 'list') => (created: { id: string }) => {
      onSelect({ kind, id: created.id });
      setTitle('');
      setCreating(null);
    };

    if (creating === 'canvas') {
      createCanvas.mutate({ title: trimmed }, { onSuccess: done('canvas') });
    } else {
      createList.mutate({ title: trimmed }, { onSuccess: done('list') });
    }
  };

  /** A torles NEM vonhato vissza - ezert kerdezunk ra. */
  const torolCanvas = async (canvas: Canvas): Promise<void> => {
    const ok = await confirm({
      title: 'Canvas törlése',
      body: (
        <>
          A(z) <strong>„{canvas.title}”</strong> canvas eltűnik a csatornából, a tartalmával együtt
          {canvas.shareCount > 0 ? ' – és mindenkinél, akivel megosztottad' : ''}. Ezt nem lehet
          visszavonni.
        </>
      ),
    });
    if (!ok) return;
    removeCanvas.mutate(canvas.id, { onSuccess: () => onDeleted('canvas', canvas.id) });
  };

  const torolLista = async (list: List): Promise<void> => {
    const ok = await confirm({
      title: 'Lista törlése',
      body: (
        <>
          A(z) <strong>„{list.title}”</strong> lista eltűnik a csatornából, mind a {list.totalCount}{' '}
          tételével együtt. Ezt nem lehet visszavonni.
        </>
      ),
    });
    if (!ok) return;
    removeList.mutate(list.id, { onSuccess: () => onDeleted('list', list.id) });
  };

  const ures = (canvases?.length ?? 0) === 0 && (lists?.length ?? 0) === 0;
  /** A sajat canvasok - kiemeles, sorrend, torles csak ezekre vonatkozik. */
  const own = canvases?.filter((c) => !c.viaShare) ?? [];
  const sharedIn = canvases?.filter((c) => c.viaShare) ?? [];

  return (
    <aside
      className={[
        // Mobilon a teljes szelesseg, asztalon rogzitett oszlop a tartalom mellett.
        'w-full shrink-0 flex-col border-r border-border-soft bg-surface sm:w-64',
        hidden ? 'hidden' : 'flex',
      ].join(' ')}
    >
      <div className="flex items-center justify-between gap-2 px-4 py-3">
        <h2 className="min-w-0 flex-1 truncate font-display text-sm font-semibold">
          {channelLabel}
        </h2>

        {/*
          BEZARAS mobilon.

          Asztalon a jobb oldali fejlecben van a gomb - de mobilon, amig a
          lista latszik, az a fejlec el van rejtve. Enelkul csak az Escape
          maradna kijaratnak, amirol egy telefonon senki nem tud.
        */}
        <button
          type="button"
          onClick={onClose}
          title="Bezárás"
          aria-label="Bezárás"
          className="grid size-8 shrink-0 place-items-center rounded-control text-ink-faint transition-colors duration-(--duration-quick) hover:bg-surface-hover hover:text-ink sm:hidden"
        >
          <Icon name="bezaras" />
        </button>

        {canEdit && (
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <button
                type="button"
                title="Új canvas vagy lista"
                aria-label="Új canvas vagy lista"
                className="rounded-control p-1 text-ink-faint transition-colors duration-(--duration-quick) hover:bg-surface-hover hover:text-ink"
              >
                <Icon name="uj" />
              </button>
            </DropdownMenu.Trigger>

            <DropdownMenu.Portal>
              <DropdownMenu.Content
                align="end"
                sideOffset={6}
                onCloseAutoFocus={(event) => {
                  if (!focusTitle.current) return;
                  focusTitle.current = false;
                  event.preventDefault();
                }}
                className="animate-fade-rise z-50 min-w-56 rounded-card border border-border-soft bg-surface-raised p-1 shadow-(--shadow-modal)"
              >
                <DropdownMenu.Item
                  onSelect={() => {
                    focusTitle.current = true;
                    setCreating('canvas');
                  }}
                  className="flex cursor-pointer items-start gap-2.5 rounded-control px-2.5 py-2 text-sm outline-none data-[highlighted]:bg-surface-hover"
                >
                  <span aria-hidden="true" className="mt-0.5 text-ink-faint">
                    <Icon name="canvas" />
                  </span>
                  <span>
                    <span className="block font-medium">Canvas</span>
                    <span className="block text-2xs text-ink-faint">
                      Közös dokumentum — leírás, tudnivalók
                    </span>
                  </span>
                </DropdownMenu.Item>

                <DropdownMenu.Item
                  onSelect={() => {
                    focusTitle.current = true;
                    setCreating('list');
                  }}
                  className="flex cursor-pointer items-start gap-2.5 rounded-control px-2.5 py-2 text-sm outline-none data-[highlighted]:bg-surface-hover"
                >
                  <span aria-hidden="true" className="mt-0.5 text-ink-faint">
                    <Icon name="listaPontos" />
                  </span>
                  <span>
                    <span className="block font-medium">Lista</span>
                    <span className="block text-2xs text-ink-faint">
                      Pipálható tételek felelőssel és határidővel
                    </span>
                  </span>
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        )}
      </div>

      {creating && (
        <div className="px-3 pb-2">
          <input
            aria-label={creating === 'canvas' ? 'A canvas címe' : 'A lista címe'}
            ref={titleRef}
            autoFocus
            value={title}
            maxLength={MAX_CANVAS_TITLE_LENGTH}
            onChange={(event) => setTitle(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') submit();
              if (event.key === 'Escape') {
                setCreating(null);
                setTitle('');
              }
            }}
            onBlur={() => {
              if (!title.trim()) setCreating(null);
            }}
            placeholder={creating === 'canvas' ? 'A canvas címe…' : 'A lista címe…'}
            className="w-full rounded-control border border-border bg-surface-raised px-2 py-1.5 text-sm outline-none focus:border-accent"
          />
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-2 pb-3">
        {isLoading && <p className="px-2 py-3 text-xs text-ink-faint">Betöltés…</p>}

        {!isLoading && ures && !creating && (
          <p className="px-2 py-3 text-xs text-ink-faint">
            {personal
              ? 'Még nincs saját canvasod. A ＋ gombbal hozhatsz létre egyet, és meg is oszthatod.'
              : 'Még nincs canvas vagy lista ebben a csatornában.'}
          </p>
        )}

        {/*
          KIEMELT canvasok elol, kulon szakaszban; alattuk a tobbi. A sorrend
          huzassal allithato (Alt + fel/le nyillal is), es a csatorna minden
          tagjanal ugyanigy latszik. Huzni a sajat szakaszon belul lehet - a
          kiemeles a csillaggal (a canvas fejleceben) valt.
        */}
        {[
          { key: 'primary', label: 'Kiemelt', items: own.filter((c) => c.isPrimary) },
          { key: 'rest', label: 'Canvasok', items: own.filter((c) => !c.isPrimary) },
        ].map(
          (group) =>
            group.items.length > 0 && (
              <div key={group.key}>
                <SectionLabel>{group.label}</SectionLabel>
                <SortableRows
                  items={group.items}
                  enabled={canEdit}
                  onReorder={(ids) =>
                    reorder.mutate(
                      group.key === 'primary'
                        ? [...ids, ...own.filter((c) => !c.isPrimary).map((c) => c.id)]
                        : [...own.filter((c) => c.isPrimary).map((c) => c.id), ...ids],
                    )
                  }
                >
                  {(canvas) => (
                    <>
                      <button
                        type="button"
                        onClick={() => onSelect({ kind: 'canvas', id: canvas.id })}
                        className={[
                          'flex w-full flex-col gap-0.5 rounded-control px-2 py-2 text-left transition-colors duration-(--duration-quick)',
                          canEdit ? 'pr-8' : '',
                          selected?.kind === 'canvas' && selected.id === canvas.id
                            ? 'bg-surface-hover'
                            : 'hover:bg-surface-hover',
                        ].join(' ')}
                      >
                        <span className="flex items-center gap-1.5">
                          {canvas.isPrimary && (
                            <span
                              role="img"
                              aria-label="A csatorna kiemelt canvasa"
                              className="text-accent"
                            >
                              <Icon name="csillag" size="sm" />
                            </span>
                          )}
                          <span className="truncate text-sm font-medium">{canvas.title}</span>
                          {canvas.shareCount > 0 && (
                            <span
                              role="img"
                              aria-label={`Megosztva (${canvas.shareCount})`}
                              title={`Megosztva (${canvas.shareCount})`}
                              className="shrink-0 text-ink-faint"
                            >
                              <Icon name="tagok" size="sm" />
                            </span>
                          )}
                        </span>

                        {canvas.preview && (
                          <span className="line-clamp-2 text-2xs text-ink-faint">
                            {canvas.preview}
                          </span>
                        )}
                        <span className="text-2xs text-ink-faint">
                          {canvas.updatedBy ? `${nameOf(canvas.updatedBy)} · ` : ''}
                          {formatDay(canvas.updatedAt)}
                        </span>
                      </button>
                      {canEdit && (
                        <DeleteItemButton
                          label="Canvas törlése"
                          onClick={() => void torolCanvas(canvas)}
                        />
                      )}
                    </>
                  )}
                </SortableRows>
              </div>
            ),
        )}

        {/*
          MASHONNAN megosztott szemelyes canvasok: a sajat jegyzetekben a nekem
          (vagy egy csoportomnak) megosztottak, csatornaban a csatornaval
          megosztottak. Szerkeszteni lehet, de a helyuket (kiemeles, sorrend,
          torles) a tulajdonos donti el.
        */}
        {sharedIn.length > 0 && (
          <>
            <SectionLabel>{personal ? 'Velem megosztva' : 'Megosztva a csatornával'}</SectionLabel>
            {sharedIn.map((canvas) => (
              <button
                key={canvas.id}
                type="button"
                onClick={() => onSelect({ kind: 'canvas', id: canvas.id })}
                className={[
                  'flex w-full flex-col gap-0.5 rounded-control px-2 py-2 text-left transition-colors duration-(--duration-quick)',
                  selected?.kind === 'canvas' && selected.id === canvas.id
                    ? 'bg-surface-hover'
                    : 'hover:bg-surface-hover',
                ].join(' ')}
              >
                <span className="flex items-center gap-1.5">
                  <span aria-hidden="true" className="text-ink-faint">
                    <Icon name="megosztas" size="sm" />
                  </span>
                  <span className="truncate text-sm font-medium">{canvas.title}</span>
                </span>
                {canvas.preview && (
                  <span className="line-clamp-2 text-2xs text-ink-faint">{canvas.preview}</span>
                )}
                <span className="text-2xs text-ink-faint">
                  {canvas.createdBy ? `${nameOf(canvas.createdBy)} canvasa · ` : ''}
                  {formatDay(canvas.updatedAt)}
                </span>
              </button>
            ))}
          </>
        )}

        {(lists?.length ?? 0) > 0 && <SectionLabel>Listák</SectionLabel>}

        {lists?.map((list) => (
          <div key={list.id} className="group relative">
            <button
              type="button"
              onClick={() => onSelect({ kind: 'list', id: list.id })}
              className={[
                'flex w-full flex-col gap-0.5 rounded-control px-2 py-2 text-left transition-colors duration-(--duration-quick)',
                canEdit ? 'pr-8' : '',
                selected?.kind === 'list' && selected.id === list.id
                  ? 'bg-surface-hover'
                  : 'hover:bg-surface-hover',
              ].join(' ')}
            >
              <span className="flex items-center gap-1.5">
                <span aria-hidden="true" className="text-ink-faint">
                  <Icon name="listaPontos" size="sm" />
                </span>
                <span className="truncate text-sm font-medium">{list.title}</span>
              </span>

              {/* A HALADAS a listan is latszik: tipikusan ezert nyitja meg az ember. */}
              <span className="text-2xs text-ink-faint">
                {list.totalCount === 0 ? 'Üres' : `${list.doneCount}/${list.totalCount} kész`}
                {' · '}
                {formatDay(list.updatedAt)}
              </span>
            </button>
            {canEdit && (
              <DeleteItemButton label="Lista törlése" onClick={() => void torolLista(list)} />
            )}
          </div>
        ))}
      </div>
    </aside>
  );
}

/**
 * Torles-gomb egy lap-lista elemen.
 *
 * TESTVERE a kivalaszto gombnak, nem a gyereke: gomb a gombban ervenytelen
 * HTML, a kepernyoolvaso nem tudja kulon elerni, es a Szokozt sem kapta meg.
 * Egerrel csak ramutatasra latszik, billentyuzettel a fokuszra is.
 */
/**
 * HUZHATO sorok - ugyanaz a minta, mint az oldalsav csatornainal (Sidebar.tsx):
 * nativ drag & drop, a celsor tetejen vekony vonal, billentyuzetrol Alt + nyil.
 * Az uj sorrendet a hivo kuldi a szervernek; a lista a valaszbol rendezodik at.
 */
function SortableRows<T extends { id: string }>({
  items,
  enabled,
  onReorder,
  children,
}: {
  items: T[];
  enabled: boolean;
  onReorder: (ids: string[]) => void;
  children: (item: T) => React.ReactNode;
}) {
  const [dragId, setDragId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);

  const clear = () => {
    setDragId(null);
    setOverId(null);
  };

  /** A `from` elemet a `to` helyere teszi, es kikuldi az uj sorrendet. */
  const move = (from: number, to: number) => {
    if (from === -1 || to < 0 || to >= items.length || from === to) return;
    const ids = items.map((item) => item.id);
    const [moved] = ids.splice(from, 1);
    ids.splice(to, 0, moved!);
    onReorder(ids);
  };

  return (
    <>
      {items.map((item, index) => (
        <div
          key={item.id}
          draggable={enabled}
          title={enabled ? 'Húzd a sorrend módosításához (vagy Alt + ↑/↓)' : undefined}
          onDragStart={(event) => {
            setDragId(item.id);
            event.dataTransfer.effectAllowed = 'move';
            // Adat nelkul a Firefox el sem inditja a huzast.
            event.dataTransfer.setData('text/plain', item.id);
          }}
          onDragEnd={clear}
          onDragOver={(event) => {
            // Csak a sajat szakaszon belul (masik szakasz soran nincs dragId-nk).
            if (!dragId || !items.some((other) => other.id === dragId)) return;
            event.preventDefault();
            event.dataTransfer.dropEffect = 'move';
            setOverId(item.id);
          }}
          onDragLeave={() => setOverId((current) => (current === item.id ? null : current))}
          onDrop={(event) => {
            event.preventDefault();
            if (dragId && dragId !== item.id) {
              move(
                items.findIndex((other) => other.id === dragId),
                index,
              );
            }
            clear();
          }}
          onKeyDown={(event) => {
            if (!enabled || !event.altKey) return;
            const delta = event.key === 'ArrowUp' ? -1 : event.key === 'ArrowDown' ? 1 : 0;
            if (delta === 0) return;
            event.preventDefault();
            move(index, index + delta);
          }}
          className={[
            'group relative rounded-control transition-opacity duration-(--duration-quick)',
            dragId === item.id ? 'opacity-40' : '',
          ].join(' ')}
        >
          {/* Hova esik: vekony vonal a celsor tetejen. */}
          {overId === item.id && dragId !== null && dragId !== item.id && (
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-x-1 -top-px h-0.5 rounded-pill bg-accent"
            />
          )}
          {children(item)}
        </div>
      ))}
    </>
  );
}

function DeleteItemButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      className="absolute right-1.5 top-1.5 grid size-6 place-items-center rounded-control text-ink-faint opacity-0 transition-opacity duration-(--duration-quick) hover:text-danger focus-visible:opacity-100 group-hover:opacity-100"
    >
      <Icon name="torles" size="sm" />
    </button>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="px-2 pb-1 pt-3 text-2xs font-semibold uppercase tracking-wide text-ink-faint">
      {children}
    </p>
  );
}

function CanvasHeader({
  canvas,
  me,
  canEdit,
  canShare,
  onClose,
  onBack,
}: {
  canvas: Canvas | null;
  me: PublicUser;
  canEdit: boolean;
  /** A sajat jegyzetek SAJAT canvasa - csak ezt lehet megosztani. */
  canShare: boolean;
  onClose: () => void;
  /** Mobilon: vissza a lap-listahoz. Asztalon nincs ertelme - ott latszik. */
  onBack?: (() => void) | undefined;
}) {
  const update = useUpdateCanvas();
  const { nameOf } = useUsers();
  const [draft, setDraft] = useState<string | null>(null);
  const [sharing, setSharing] = useState(false);

  const commit = () => {
    if (draft === null || !canvas) return;
    const trimmed = draft.trim();
    setDraft(null);
    if (!trimmed || trimmed === canvas.title) return;
    update.mutate({ canvasId: canvas.id, patch: { title: trimmed } });
  };

  return (
    <header className="flex items-center gap-2 border-b border-border-soft px-4 py-3 sm:px-6">
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          title="Vissza a listához"
          aria-label="Vissza a listához"
          className="-ml-1 grid size-9 shrink-0 place-items-center rounded-control text-ink-muted transition-colors duration-(--duration-quick) hover:bg-surface-hover hover:text-ink"
        >
          <Icon name="nyilBalra" />
        </button>
      )}

      {canvas ? (
        draft !== null ? (
          <input
            autoFocus
            value={draft}
            maxLength={MAX_CANVAS_TITLE_LENGTH}
            onChange={(event) => setDraft(event.target.value)}
            onBlur={commit}
            onKeyDown={(event) => {
              if (event.key === 'Enter') commit();
              if (event.key === 'Escape') setDraft(null);
            }}
            aria-label="Canvas címe"
            className="min-w-0 flex-1 rounded-control border border-border bg-surface px-2 py-1 font-display text-lg font-semibold outline-none focus:border-accent"
          />
        ) : (
          <h2 className="min-w-0 flex-1 truncate font-display text-lg font-semibold">
            {/* Az atnevezes GOMB, hogy billentyuzettel is elerheto legyen. */}
            {canEdit ? (
              <button
                type="button"
                onClick={() => setDraft(canvas.title)}
                title="Kattints az átnevezéshez"
                className="block max-w-full cursor-text truncate rounded-control px-2 py-1 text-left transition-colors duration-(--duration-quick) hover:bg-surface-hover"
              >
                {canvas.title}
              </button>
            ) : (
              canvas.title
            )}
          </h2>
        )
      ) : (
        <span className="flex-1" />
      )}

      {/* Mashonnan megosztott canvas: kie, es hogy itt nem allithato a helye. */}
      {canvas?.viaShare && (
        <span
          title="Megosztott canvas – együtt szerkesztitek, a helyét a tulajdonosa dönti el."
          className="flex shrink-0 items-center gap-1.5 rounded-pill bg-surface-sunken px-2 py-0.5 text-2xs text-ink-muted"
        >
          <Icon name="megosztas" size="sm" />
          <span className="hidden sm:inline">
            {canvas.createdBy ? `${nameOf(canvas.createdBy)} osztotta meg` : 'Megosztva'}
          </span>
        </span>
      )}

      {canvas && canShare && (
        <button
          type="button"
          onClick={() => setSharing(true)}
          title="Megosztás kollégával, csatornával vagy csoporttal"
          className={[
            'flex items-center gap-1.5 rounded-control px-2 py-1 text-xs transition-colors duration-(--duration-quick) hover:bg-surface-hover',
            canvas.shareCount > 0 ? 'text-accent' : 'text-ink-faint hover:text-ink',
          ].join(' ')}
        >
          <Icon name="tagok" size="sm" />
          {canvas.shareCount > 0 ? `Megosztva (${canvas.shareCount})` : 'Megosztás'}
        </button>
      )}

      {sharing && canvas && (
        <ShareCanvasDialog canvas={canvas} me={me} onClose={() => setSharing(false)} />
      )}

      {/* Akarhany canvas lehet kiemelt - a gomb ki-be kapcsol. */}
      {canvas && canEdit && !canvas.viaShare && (
        <button
          type="button"
          aria-pressed={canvas.isPrimary}
          onClick={() =>
            update.mutate({ canvasId: canvas.id, patch: { isPrimary: !canvas.isPrimary } })
          }
          title={
            canvas.isPrimary
              ? 'A kiemelés levétele (a canvas a többi közé kerül)'
              : 'Kiemelés: a canvas a lista tetejére, a „Kiemelt” szakaszba kerül'
          }
          className={[
            'flex items-center gap-1.5 rounded-control px-2 py-1 text-xs transition-colors duration-(--duration-quick) hover:bg-surface-hover',
            canvas.isPrimary ? 'text-accent' : 'text-ink-faint hover:text-ink',
          ].join(' ')}
        >
          <Icon name="csillag" size="sm" />
          {canvas.isPrimary ? 'Kiemelve' : 'Kiemelés'}
        </button>
      )}

      <button
        type="button"
        onClick={onClose}
        title="Bezárás"
        className="rounded-control p-1.5 text-ink-faint transition-colors duration-(--duration-quick) hover:bg-surface-hover hover:text-ink"
      >
        <Icon name="bezaras" />
      </button>
    </header>
  );
}

function EmptyState({ hasAny, isLoading }: { hasAny: boolean; isLoading: boolean }) {
  if (isLoading) return <div className="flex-1" />;

  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-2 px-6 text-center">
      <span aria-hidden className="text-ink-faint">
        <Icon name="canvas" size="xl" />
      </span>
      <p className="text-sm text-ink-muted">
        {hasAny ? 'Válassz egy canvast a listából.' : 'Hozz létre egy canvast a ＋ gombbal.'}
      </p>
      <p className="max-w-sm text-xs text-ink-faint">
        A canvas a csatorna közös jegyzetlapja: ügyeleti rend, gyakori kérdések, fontos
        elérhetőségek. Többen szerkeszthetitek egyszerre.
      </p>
    </div>
  );
}
